import React from "react";
import PropTypes from "prop-types";
import { Typography, Box } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { COLORS } from "../constants";
import { Btn, Inp, CollapsibleSection, Badge } from "../components/UI";
import { ServiceForm, ResourceForm } from "../components/Forms";
import ServiceDetailInfo from "../components/ServiceDetailInfo";
import { useStyles } from "./template-editor-view.style";

function getServiceLabel(item, servicesRef) {
  if (!item) return "";
  if (item.serviceId != null && servicesRef?.length) {
    const ref = servicesRef.find((service) => service.id === item.serviceId);
    return ref ? (ref.displayName ?? ref.name) : String(item.serviceId);
  }
  return item.serviceId == null ? "" : String(item.serviceId);
}

function getResourceLabel(item, resourcesRef) {
  if (!item) return "";
  if (item.resourceId != null && resourcesRef?.length) {
    const ref = resourcesRef.find((resource) => resource.id === item.resourceId);
    return ref ? ref.name : String(item.resourceId);
  }
  return item.resourceId == null ? "" : String(item.resourceId);
}

function getStatusInfo(isService, item) {
  const isActive = isService ? item.available : item.active;
  if (isActive) return { statusType: "active", statusLabel: "Active" };
  if (isService) return { statusType: "suspended", statusLabel: "Suspended" };
  return { statusType: "inactive", statusLabel: "Inactive" };
}

function AccordionItem({ type, item, index, isOpen, onToggle, onUpdate, onRemove, servicesRef, resourcesRef }) {
  const classes = useStyles();
  const isService = type === "svc";
  const label = isService ? getServiceLabel(item, servicesRef) : getResourceLabel(item, resourcesRef);
  const fallbackLabel = isService ? `Service #${index + 1}` : `Resource #${index + 1}`;
  const { statusType, statusLabel } = getStatusInfo(isService, item);

  return (
    <Box className={classes.accordionCard}>
      <button
        type="button"
        onClick={() => onToggle(index)}
        className={`${classes.accordionHeader} ${isOpen ? classes.accordionHeaderOpen : classes.accordionHeaderClosed}`}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginBottom: '10px', flexWrap: "wrap" }}>
            <Typography component="span" sx={{ fontSize: 15, fontWeight: 600, color: COLORS.body, letterSpacing: "0.01em" }}>
              {label || fallbackLabel}
            </Typography>
            <Badge type={statusType}>{statusLabel}</Badge>
            {!isService && item.autoAssign && <Badge type="tag">Auto-Assign</Badge>}
          </Box>
          <Box sx={{ display: "flex", gap: '20px', fontSize: 12.5, color: COLORS.muted, flexWrap: "wrap", lineHeight: 1.5, alignItems: "baseline" }}>
            {isService && <ServiceDetailInfo item={item} />}
            {!isService && (
              <Typography component="span" variant="caption">
                <Typography component="span" variant="caption" sx={{ color: COLORS.muted }}>Pathways:</Typography>{" "}
                <Typography component="span" variant="caption" sx={{ color: COLORS.body, fontWeight: 500 }}>{item.hidden ? "Hidden" : "Visible"}</Typography>
              </Typography>
            )}
          </Box>
        </Box>
        <Typography component="span" className={classes.expandCollapseLabel}>
          {isOpen ? "Collapse" : "Expand"}
          {isOpen ? (
            <ExpandLessIcon className={classes.expandCollapseIcon} aria-hidden />
          ) : (
            <ExpandMoreIcon className={classes.expandCollapseIcon} aria-hidden />
          )}
        </Typography>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onRemove(index);
          }}
          className={classes.deleteButton}
          aria-label={`Remove ${label || fallbackLabel}`}
        >
          ✕
        </button>
      </button>
      {isOpen && (
        <Box className={classes.accordionBody}>
          {isService && (
            <ServiceForm
              data={item}
              onChange={(data) => onUpdate(index, data)}
              servicesRef={servicesRef}
              displayNameWhenNoRefs={label || fallbackLabel}
            />
          )}
          {!isService && (
            <ResourceForm
              data={item}
              onChange={(data) => onUpdate(index, data)}
              resourcesRef={resourcesRef}
              displayNameWhenNoRefs={label || fallbackLabel}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

AccordionItem.propTypes = {
  type: PropTypes.string.isRequired,
  item: PropTypes.shape({
    serviceId: PropTypes.number,
    resourceId: PropTypes.number,
    available: PropTypes.bool,
    active: PropTypes.bool,
    availType: PropTypes.string,
    sso: PropTypes.bool,
    autoProv: PropTypes.bool,
    externalId: PropTypes.string,
    explorable: PropTypes.bool,
    autoAssign: PropTypes.bool,
    hidden: PropTypes.bool,
  }).isRequired,
  index: PropTypes.number.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  servicesRef: PropTypes.array,
  resourcesRef: PropTypes.array,
};

AccordionItem.defaultProps = {
  servicesRef: [],
  resourcesRef: [],
};

export function TemplateEditorView({
  resourcesRef,
  servicesRef,
  editingTemplate,
  setEditingTemplate,
  onBack,
  onSave,
  templateExpandedServices,
  templateExpandedResources,
  toggleExpandedService,
  toggleExpandedResource,
  templateUpdateService,
  templateRemoveService,
  templateAddService,
  templateUpdateResource,
  templateRemoveResource,
  templateAddResource,
}) {
  const classes = useStyles();
  const [servicesSectionExpanded, setServicesSectionExpanded] = React.useState(true);
  const [resourcesSectionExpanded, setResourcesSectionExpanded] = React.useState(true);

  if (!editingTemplate) return null;

  return (
    <Box className={classes.root}>
      <Box className={classes.toolbar}>
        <Box className={classes.toolbarLeft}>
          <button
            type="button"
            onClick={onBack}
            className={classes.backButton}
          >
            ← Back to Library
          </button>
          <Typography component="span" sx={{ color: COLORS.border }}>|</Typography>
          <Typography component="span" sx={{ fontSize: 15, fontWeight: 700, color: COLORS.body }}>
            {editingTemplate.id ? "Edit Template" : "New Template"}
          </Typography>
        </Box>
        <Box className={classes.toolbarActions}>
          <Btn variant="outlined" size="small" onClick={onBack}>
            Cancel
          </Btn>
          <Btn size="small" onClick={onSave}>
            Save Template
          </Btn>
        </Box>
      </Box>
      <Box className={classes.scrollArea}>
        <Box className={classes.editorContainer}>
          <Box className={classes.metaCard}>
            <Box className={classes.metaGrid}>
              <Inp
                label="Template Name"
                value={editingTemplate.name}
                onChange={(val) => setEditingTemplate((prev) => ({ ...prev, name: val }))}
              />
              <Inp
                label="Description"
                value={editingTemplate.desc}
                onChange={(val) => setEditingTemplate((prev) => ({ ...prev, desc: val }))}
              />
            </Box>
            <Box className={classes.metaStats}>
              <Typography component="span" variant="caption">
                {editingTemplate.services.length} service{editingTemplate.services.length === 1 ? "" : "s"}
              </Typography>
              <Typography component="span" variant="caption">
                {editingTemplate.resources.length} resource{editingTemplate.resources.length === 1 ? "" : "s"}
              </Typography>
            </Box>
          </Box>
          <CollapsibleSection
            title={`Services (${editingTemplate.services.length})`}
            right={<Btn variant="text" size="small" onClick={templateAddService}>+ Add Service</Btn>}
            expanded={servicesSectionExpanded}
            onToggle={() => setServicesSectionExpanded((prev) => !prev)}
          >
            {editingTemplate.services.length === 0 && (
              <Typography className={classes.emptyState}>
                No services. Click &quot;+ Add Service&quot; above.
              </Typography>
            )}
            {editingTemplate.services.map((item, origIndex) => (
              <AccordionItem
                key={item._key || `svc-${origIndex}`}
                type="svc"
                item={item}
                index={origIndex}
                isOpen={templateExpandedServices.has(origIndex)}
                onToggle={toggleExpandedService}
                onUpdate={templateUpdateService}
                onRemove={templateRemoveService}
                servicesRef={servicesRef}
                resourcesRef={resourcesRef}
              />
            ))}
          </CollapsibleSection>
          <CollapsibleSection
            title={`Resources (${editingTemplate.resources.length})`}
            right={<Btn variant="text" size="small" onClick={templateAddResource}>+ Add Resource</Btn>}
            expanded={resourcesSectionExpanded}
            onToggle={() => setResourcesSectionExpanded((prev) => !prev)}
          >
            {editingTemplate.resources.length === 0 && (
              <Typography className={classes.emptyState}>
                No resources. Click &quot;+ Add Resource&quot; above.
              </Typography>
            )}
            {editingTemplate.resources.map((item, origIndex) => (
              <AccordionItem
                key={item._key || `res-${origIndex}`}
                type="res"
                item={item}
                index={origIndex}
                isOpen={templateExpandedResources.has(origIndex)}
                onToggle={toggleExpandedResource}
                onUpdate={templateUpdateResource}
                onRemove={templateRemoveResource}
                servicesRef={servicesRef}
                resourcesRef={resourcesRef}
              />
            ))}
          </CollapsibleSection>
        </Box>
      </Box>
    </Box>
  );
}

TemplateEditorView.propTypes = {
  resourcesRef: PropTypes.array,
  servicesRef: PropTypes.array,
  editingTemplate: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    desc: PropTypes.string,
    services: PropTypes.array,
    resources: PropTypes.array,
  }),
  setEditingTemplate: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  templateExpandedServices: PropTypes.instanceOf(Set),
  templateExpandedResources: PropTypes.instanceOf(Set),
  toggleExpandedService: PropTypes.func.isRequired,
  toggleExpandedResource: PropTypes.func.isRequired,
  templateUpdateService: PropTypes.func.isRequired,
  templateRemoveService: PropTypes.func.isRequired,
  templateAddService: PropTypes.func.isRequired,
  templateUpdateResource: PropTypes.func.isRequired,
  templateRemoveResource: PropTypes.func.isRequired,
  templateAddResource: PropTypes.func.isRequired,
};

TemplateEditorView.defaultProps = {
  resourcesRef: [],
  servicesRef: [],
  editingTemplate: null,
  templateExpandedServices: new Set(),
  templateExpandedResources: new Set(),
};
